/**
 * Ein Zahlenfeld, das erst beim Verlassen übernimmt.
 *
 * Vorher waren viele Felder gesteuert und schrieben bei jedem Tastendruck:
 * Wer die Neigung leerte, um neu zu tippen, hatte kurz ein Flachdach; wer bei
 * einer Komponentenanzahl die 1 löschte und 3 tippte, bekam 13. Hier gilt:
 * tippen ist tippen, übernommen wird bei Enter oder beim Verlassen, und was
 * nicht passt, wird zurückgesetzt und gesagt.
 */

import { useEffect, useRef, useState } from 'react';

import { leseZahl, zahlText, type Zahlgrenzen } from '../zahl.ts';

export function Zahlfeld({
  wert,
  onWert,
  leerErlaubt = false,
  stellen = 3,
  ...grenzenUndRest
}: {
  wert: number | null | undefined;
  onWert: (wert: number | null) => void;
  /** Leer übernimmt `null` („nicht eingetragen“) statt den alten Wert zu behalten. */
  leerErlaubt?: boolean;
  stellen?: number;
} & Zahlgrenzen &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'onBlur' | 'type' | 'min' | 'max'>): React.JSX.Element {
  const { min, max, ganz, ...rest } = grenzenUndRest;
  const [text, setText] = useState(zahlText(wert, stellen));
  const [fehler, setFehler] = useState<string | null>(null);
  const tippt = useRef(false);

  // Ändert sich der Wert von außen (Abgleich, anderer Knopf), zeigt das Feld
  // ihn — außer während jemand darin tippt.
  useEffect(() => {
    if (!tippt.current) setText(zahlText(wert, stellen));
  }, [wert, stellen]);

  const uebernehmen = (): void => {
    tippt.current = false;
    const neu = leseZahl(text, { ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}), ...(ganz ? { ganz } : {}) });
    if (neu === null || (neu === undefined && !leerErlaubt)) {
      setFehler(
        neu === undefined
          ? 'Leer geht hier nicht — der alte Wert bleibt.'
          : `Keine gültige Zahl${min !== undefined || max !== undefined ? ` (${min ?? '…'} bis ${max ?? '…'})` : ''} — der alte Wert bleibt.`,
      );
      setText(zahlText(wert, stellen));
      return;
    }
    setFehler(null);
    const ergebnis = neu ?? null;
    setText(zahlText(ergebnis, stellen));
    if (ergebnis !== (wert ?? null)) onWert(ergebnis);
  };

  return (
    <>
      <input
        {...rest}
        type="text"
        inputMode={ganz ? 'numeric' : 'decimal'}
        value={text}
        aria-invalid={fehler ? true : undefined}
        onChange={(ereignis) => {
          tippt.current = true;
          setText(ereignis.target.value);
        }}
        onBlur={uebernehmen}
        onKeyDown={(ereignis) => {
          if (ereignis.key === 'Enter') (ereignis.target as HTMLInputElement).blur();
        }}
      />
      {fehler && <span className="klein" style={{ display: 'block', color: 'var(--fehler)' }}>{fehler}</span>}
    </>
  );
}
